import { adminPaymentStatus, isClosedAdminPayment, canShowAdminReceipt, adminDisplayFee, adminDisplayMessage, adminTransactionCount } from '../utils/paymentDisplay';
import { readPaymentList } from '../../Common/api/paymentResultRead';
import PaymentStatusFilter from '../../Common/components/PaymentStatusFilter';
import PaymentResultMonitor from '../../Common/components/PaymentResultMonitor';
import { isUnknownPayment, unknownPaymentLabel, safePaymentResultMessage, paymentResultFields } from '../../Common/utils/paymentResult';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Check, CreditCard, Eye, HelpCircle, Pencil, X } from 'lucide-react';
import { Tooltip } from 'react-tooltip';
import ManagementPage from '../../Common/components/common/ManagementPage';
import ReceiptImageActions from '../../Common/components/receipt/ReceiptImageActions';
import ReceiptPreviewModalV2 from '../../Common/components/receipt/ReceiptPreviewModalV2';
import { resolveUnmeReceiptInfo, receiptSendError } from '../../Common/api/unmeReceiptInfo';
import { RECEIPT_UI_VERSION } from '../../Common/config/receiptUi';
import { RECEIPT_CARD_SMS_NOTICE, RECEIPT_LABELS } from '../../Common/constants/receiptLabels';
import { useToast } from '../../Common/context/ToastContext';
import { downloadBlob } from '../../Common/api/client';
import { useTooltip } from '../../Common/context/TooltipContext';
import { ADMIN_UNME_FALLBACK_ENABLED, TRANSACTION_PHARMACY_NAME_EDIT_ENABLED } from '../../Common/config/menuConfig';
import { adminPaymentContractLabel } from '../utils/paymentContract';
import { unmeContractColor } from '../../Common/utils/unmeContractColor';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const baseUrl = process.env.REACT_APP_API || '';
const RECEIPT_SMS_ENABLED = Number(process.env.REACT_APP_RECEIPT_SMS_ENABLED ?? 0) === 1;
const RECEIPT_KAKAO_ENABLED = Number(process.env.REACT_APP_RECEIPT_KAKAO_ENABLED ?? 0) === 1;
const TRANSACTION_STATUS_LABELS = {
  COMPLETED: '승인',
  CANCELED: '취소',
  FAILED: '실패',
  REQUESTED: '요청 중',
  APPROVED: '승인 처리 중',
};

/** 전표 공급자 정보 (통합정산 전표용) */
const RECEIPT_SUPPLIER_INFO = {
  supplierName: '-',
  supplierOwnerName: '-',
  supplierBusinessNo: '-',
  supplierContact: '-',
  supplierAddress: '-',
};

/** 전표 카드 가맹점 정보 (통합정산 전표용) */
const RECEIPT_CARD_MERCHANT_INFO = {
  cardMerchantName: '-',
  cardMerchantOwnerName: '-',
  cardMerchantBusinessNo: '-',
  cardMerchantContact: '-',
  cardMerchantAddress: '-',
};

// UTC → KST 포맷
function formatKST(dateStr, format = 'YYYY.MM.DD. HH:mm') {
  if (!dateStr) return '-';
  return dayjs.utc(dateStr).add(9, 'hour').format(format);
}

/** 최대 길이 초과 시 ... 처리 (줄바꿈 방지용) */
function truncateStr(str, maxLen) {
  if (str == null || str === '') return '-';
  const s = String(str).trim();
  if (s.length === 0) return '-';
  if (s.length <= maxLen) return s;
  return s.slice(0, maxLen) + '...';
}

// pg_raw_response에서 카드명 추출 (AppPaymentList와 동일 패턴)
function parseCardNameFromRaw(pgRawResponse) {
  if (!pgRawResponse || typeof pgRawResponse !== 'string') return '-';
  try {
    const parsed = JSON.parse(pgRawResponse);
    const data = parsed?.data;
    return data?.cardNm || data?.cardKind || '-';
  } catch {
    return '-';
  }
}

function toFiniteNumber(value, fallback = 0) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null);
}

function getSignedTransactionAmount(tx) {
  const amount = toFiniteNumber(tx?.amount);
  return tx?.transactionType === 'CANCEL' ? -Math.abs(amount) : amount;
}

function getSummaryTotalAmount(apiSummary, mappedRows, totalNum) {
  const netPaymentAmount = firstDefined(
    apiSummary.netPaymentAmount,
    apiSummary.net_payment_amount,
    apiSummary.netAmount,
    apiSummary.net_amount
  );
  if (netPaymentAmount != null) return toFiniteNumber(netPaymentAmount);

  const paymentAmount = firstDefined(
    apiSummary.approvalAmount,
    apiSummary.approval_amount,
    apiSummary.paymentAmount,
    apiSummary.payment_amount
  );
  const cancelAmount = firstDefined(
    apiSummary.cancelAmount,
    apiSummary.cancel_amount
  );
  if (paymentAmount != null || cancelAmount != null) {
    return toFiniteNumber(paymentAmount) - Math.abs(toFiniteNumber(cancelAmount));
  }

  if (apiSummary.completedAmount != null || apiSummary.completed_amount != null) return toFiniteNumber(apiSummary.completedAmount ?? apiSummary.completed_amount);
  if (totalNum <= mappedRows.length) {
    return mappedRows.reduce(
      (sum, row) => sum + (isUnknownPayment(row) || isClosedAdminPayment(row) ? 0 : getSignedTransactionAmount(row)),
      0
    );
  }

  return 0;
}

// 관리자 결제목록 항목 매핑
function mapPaymentTransaction(item) {
  const paymentTransactionId =
    item.payment_transaction_id ??
    item.paymentTransactionId ??
    item.id ??
    item.paymentId ??
    item.payment_id;
  const rowId =
    item.transaction_id ??
    item.transactionId ??
    [
      paymentTransactionId ?? 'payment',
      item.transaction_type ?? item.transactionType ?? 'UNKNOWN',
      item.business_created_at ?? item.businessCreatedAt ?? '',
    ].join(':');

  const businessDate =
    item.business_created_at ??
    item.businessCreatedAt;
  const dateStr =
    businessDate ??
    item.approved_at ??
    item.approvedAt ??
    item.approval_date ??
    item.approvalDate ??
    item.created_at ??
    item.createdAt ??
    item.date;
  const date = dateStr
    ? businessDate
      ? dayjs.utc(dateStr).subtract(9, 'hour').toISOString()
      : dayjs.utc(dateStr).toISOString()
    : dayjs().toISOString();

  const instmtMon =
    item.instmt_mon ??
    item.instmtMon ??
    item.pg_instmt_mon ??
    item.installmentMonths ??
    item.installment_months ??
    '00';
  const type = instmtMon === '00' ? '일시불' : `${instmtMon}개월`;

  const cardName =
    item.card_nm ??
    item.cardNm ??
    item.cardName ??
    parseCardNameFromRaw(item.pg_raw_response) ??
    '-';

  const status = item.status || '';
  const transactionType = String(
    item.transaction_type ?? item.transactionType ?? ''
  ).toUpperCase();
  const transactionStatus = adminPaymentStatus(item);
  const amount = getSignedTransactionAmount({
    amount:
      item.amount ??
      item.trdAmt ??
      item.trd_amt ??
      item.pg_trd_amt ??
      item.request_trd_amt ??
      0,
    transactionType,
  });

  return {
    ...paymentResultFields(item),
    id: rowId,
    paymentTransactionId,
    contractLabel: ADMIN_UNME_FALLBACK_ENABLED ? adminPaymentContractLabel(item) : null,
    receiptTransactionId: item.transaction_id ?? item.transactionId ?? item.payment_transaction_id ?? item.paymentTransactionId ?? item.id,
    date,
    pharmacy:
      item.transaction_pharmacy_name ??
      item.transactionPharmacyName ??
      item.mcht_cust_nm ??
      item.mchtCustNm ??
      item.pharmacy ??
      item.customer_name ??
      item.pharmacy_name ??
      item.customerName ??
      item.pharmacyName ??
      '-',
    amount,
    approvalNo:
      item.appr_no ??
      item.apprNo ??
      item.approvalNo ??
      item.approval_no ??
      item.approvalNumber ??
      item.original_appr_no ??
      item.originalApprNo ??
      '',
    fee: Number(adminDisplayFee(item)),
    canShowReceipt: canShowAdminReceipt(item),
    display_status: item.display_status,
    display_message: item.display_message,
    raw_status: item.raw_status,
    payment_source: item.payment_source,
    type,
    status,
    transactionType,
    transactionStatus,
    resultCode:
      item.out_rslt_cd ??
      item.outRsltCd ??
      '',
    resultMessage: adminDisplayMessage(item),
    cardName,
    pgProvider:
      item.pg_provider ??
      item.pgProvider ??
      item.provider ??
      '',
    cardNo:
      item.card_no_masked ??
      item.cardNoMasked ??
      '',
    merchant: item.cp_name ?? item.merchant ?? '-',
    driver:
      item.salesperson_name ??
      item.salespersonName ??
      item.salesperson ??
      item.driver ??
      item.driver_name ??
      item.driverName ??
      item.terminal_name ??
      item.terminalName ??
      '',
    cancelStatus:
      String(status).toUpperCase() === 'CANCELED' || item.canceled_at != null,
    canceledAt: item.canceled_at ?? item.canceledAt ?? null,
    // 가맹점 정보 (전표용) - cp_* (채널파트너/가맹점) 우선
    transactionPharmacyName:
      item.transaction_pharmacy_name ?? item.transactionPharmacyName ?? null,
    cpName: item.cp_name ?? item.cpName ?? null,
    cpOwnerName: item.cp_owner_name ?? item.cpOwnerName ?? null,
    cpBusinessNo: item.cp_business_no ?? item.cpBusinessNo ?? null,
    cpPhone: item.cp_phone ?? item.cpPhone ?? null,
    cpAddress: item.cp_address ?? item.cpAddress ?? null,
    pharmacyBusinessNo:
      item.pharmacy_business_no ?? item.pharmacyBusinessNo ?? null,
    pharmacyAddress:
      item.pharmacy_address ?? item.pharmacyAddress ?? null,
    pharmacyContact:
      item.pharmacy_phone ?? item.pharmacy_contact ?? item.pharmacyContact ??
      item.mcht_contact ?? item.mchtContact ?? null,
  };
}

function getStatusLabel(tx) {
  if (isUnknownPayment(tx)) return unknownPaymentLabel(tx);
  if (isClosedAdminPayment(tx)) return '결제창 종료';
  const status = String(tx.transactionStatus || '').toUpperCase();
  return TRANSACTION_STATUS_LABELS[status] || tx.transactionStatus || '-';
}

function isCanceledApproval(tx) {
  return (
    tx.transactionType === 'APPROVAL' &&
    String(tx.status || '').toUpperCase() === 'CANCELED'
  );
}

function getCancelDateKeys(tx) {
  const keys = [];
  if (tx?.paymentTransactionId != null && tx.paymentTransactionId !== '') {
    keys.push(`payment:${tx.paymentTransactionId}`);
  }
  if (tx?.approvalNo) {
    keys.push(`approval:${tx.approvalNo}`);
  }
  return keys;
}

function buildCancelDateMap(transactions) {
  const map = new Map();
  transactions.forEach((tx) => {
    if (tx?.transactionType !== 'CANCEL') return;
    const cancelDate = tx.canceledAt || tx.date;
    if (!cancelDate) return;
    getCancelDateKeys(tx).forEach((key) => map.set(key, cancelDate));
  });
  return map;
}

function getCanceledApprovalDate(tx, cancelDateMap) {
  if (tx?.canceledAt) return tx.canceledAt;
  const matchedKey = getCancelDateKeys(tx).find((key) => cancelDateMap.has(key));
  return matchedKey ? cancelDateMap.get(matchedKey) : null;
}

// 날짜 문자열을 YYYY.MM.DD 형식으로 정규화 (26.03.03 → 2026.03.03)
const normalizeDateStr = (s) => {
  if (!s || typeof s !== 'string') return '';
  const t = s.trim().replace(/\.$/, '');
  const parts = t.split('.');
  if (parts.length < 3) return t;
  let [y, m, d] = parts;
  if (y && y.length === 2) y = '20' + y;
  return `${y}.${String(m).padStart(2, '0')}.${String(d).padStart(2, '0')}`;
};

function PaymentList() {
  const { showToast } = useToast();
  const { showAllOpen } = useTooltip();
  const adminSupplierInfo = useSelector((state) => state.adminSupplierInfo?.info);
  const adminPgMerchantRows = useSelector((state) => state.adminPgMerchantInfo?.rows) || [];
  const [hoveredTooltipId, setHoveredTooltipId] = useState(null);
  const location = useLocation();
  useEffect(() => {
    if (!showAllOpen) setHoveredTooltipId(null);
  }, [showAllOpen]);

  // 기본 기간: 당월 1일 ~ 오늘 (또는 location.state.searchDate가 있으면 해당 날짜)
  const getDefaultMonthRange = () => {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const formatDate = (d) => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;

    // 정산관리에서 전달된 날짜가 있으면 해당 날짜로 설정 (단일: "2026.02.11", 범위: "2026.02.11~2026.02.12")
    if (location.state?.searchDate) {
      const sd = String(location.state.searchDate).trim();
      if (sd.includes('~')) {
        const [s, e] = sd.split('~').map((x) => normalizeDateStr(x)).filter(Boolean);
        return {
          start: s || formatDate(today),
          end: e || formatDate(today),
        };
      }
      const norm = normalizeDateStr(sd);
      return {
        start: norm || formatDate(today),
        end: norm || formatDate(today),
      };
    }

    return {
      start: formatDate(monthStart),
      end: formatDate(today),
    };
  };
  const defaultRange = getDefaultMonthRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);

  // 정산관리에서 searchDate로 이동 시 날짜 동기화 (location.state는 네비게이션 직후에 반영)
  useEffect(() => {
    const sd = location.state?.searchDate;
    if (!sd) return;
    const str = String(sd).trim();
    const formatDate = (d) => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    if (str.includes('~')) {
      const parts = str.split('~').map((x) => normalizeDateStr(x)).filter(Boolean);
      if (parts.length >= 2) {
        setStartDate(parts[0]);
        setEndDate(parts[1]);
      } else if (parts.length === 1) {
        setStartDate(parts[0]);
        setEndDate(parts[0]);
      }
    } else {
      const norm = normalizeDateStr(str);
      if (norm) {
        setStartDate(norm);
        setEndDate(norm);
      }
    }
  }, [location.state?.searchDate]);
  const [searchInput, setSearchInput] = useState(''); // 검색창 입력값
  const [searchQuery, setSearchQuery] = useState(''); // API q 파라미터 (검색 버튼/Enter 시 적용)
  const [resultStatusFilter, setResultStatusFilter] = useState('');
  const [rows, setRows] = useState([]);
  const cancelDateMap = useMemo(() => buildCancelDateMap(rows), [rows]);
  const [editingPharmacyId, setEditingPharmacyId] = useState(null);
  const [pharmacyNameDraft, setPharmacyNameDraft] = useState('');
  const pharmacyNameInputRef = useRef(null);
  const [pendingPharmacyNameChange, setPendingPharmacyNameChange] = useState(null);
  const [pharmacyNameReason, setPharmacyNameReason] = useState('');
  const [isPharmacyNameSaving, setIsPharmacyNameSaving] = useState(false);
  const pharmacyNameReasonInputRef = useRef(null);
  const [summary, setSummary] = useState({ totalAmount: 0, totalCount: 0 });
  const [isExcelDownloading, setIsExcelDownloading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const visiblePagesRef = useRef(1);
  visiblePagesRef.current = page;
  const [hasNext, setHasNext] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const infiniteScrollRef = useRef(null);
  const pageRef = useRef(page);
  const hasNextRef = useRef(hasNext);
  pageRef.current = page;
  hasNextRef.current = hasNext;
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState('');
  const [isSendingReceipt, setIsSendingReceipt] = useState(false);
  const [sendMethod, setSendMethod] = useState('email'); // 'email' | 'sms' | 'kakao'
  const [receiptPhone, setReceiptPhone] = useState('');
  const [receiptPreviewData, setReceiptPreviewData] = useState(null);
  const receiptInfoRequestRef = useRef(0);
  useEffect(() => () => { receiptInfoRequestRef.current += 1; }, []);
  const [receiptViewMode, setReceiptViewMode] = useState('approval');
  const [revealModalOpen, setRevealModalOpen] = useState(false);
  const [revealPassword, setRevealPassword] = useState('');
  const [revealReason, setRevealReason] = useState('');
  const [revealLoading, setRevealLoading] = useState(false);
  const receiptSupplierInfo = {
    supplierName: adminSupplierInfo?.name || RECEIPT_SUPPLIER_INFO.supplierName,
    supplierOwnerName: adminSupplierInfo?.owner_name || RECEIPT_SUPPLIER_INFO.supplierOwnerName,
    supplierBusinessNo: adminSupplierInfo?.business_no || RECEIPT_SUPPLIER_INFO.supplierBusinessNo,
    supplierContact: adminSupplierInfo?.contact || RECEIPT_SUPPLIER_INFO.supplierContact,
    supplierAddress: adminSupplierInfo?.address || RECEIPT_SUPPLIER_INFO.supplierAddress,
  };

  useEffect(() => {
    if (editingPharmacyId == null) return undefined;
    const frameId = window.requestAnimationFrame(() => {
      pharmacyNameInputRef.current?.focus();
      pharmacyNameInputRef.current?.select();
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [editingPharmacyId]);

  useEffect(() => {
    if (!pendingPharmacyNameChange) return undefined;
    const frameId = window.requestAnimationFrame(() => {
      pharmacyNameReasonInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [pendingPharmacyNameChange]);

  const startEditingPharmacyName = useCallback((item) => {
    const rowId = item.id;
    if (rowId == null) {
      showToast('거래 ID가 없어 약국명을 수정할 수 없습니다.', 'error');
      return;
    }
    setEditingPharmacyId(rowId);
    setPharmacyNameDraft(item.pharmacy === '-' ? '' : String(item.pharmacy || ''));
  }, [showToast]);

  const cancelEditingPharmacyName = useCallback(() => {
    setEditingPharmacyId(null);
    setPharmacyNameDraft('');
  }, []);

  const savePharmacyName = useCallback((item) => {
    const rowId = item.id;
    const nextPharmacyName = pharmacyNameDraft.trim();

    if (rowId == null) {
      showToast('거래 ID가 없어 약국명을 수정할 수 없습니다.', 'error');
      return;
    }

    if (!nextPharmacyName) {
      showToast('약국명을 입력해 주세요.', 'error');
      pharmacyNameInputRef.current?.focus();
      return;
    }

    const currentPharmacyName = item.pharmacy === '-' ? '' : String(item.pharmacy || '').trim();
    if (nextPharmacyName === currentPharmacyName) {
      cancelEditingPharmacyName();
      return;
    }

    setPendingPharmacyNameChange({
      id: rowId,
      previousPharmacyName: item.pharmacy || '-',
      pharmacyName: nextPharmacyName,
    });
    setPharmacyNameReason('');
  }, [cancelEditingPharmacyName, pharmacyNameDraft, showToast]);


  const getReceiptCardMerchantInfo = useCallback((pgProvider) => {
    const provider = String(pgProvider || '').toUpperCase();
    const matched = adminPgMerchantRows.find(
      (row) => String(row?.pg_provider || '').toUpperCase() === provider,
    );
    if (!matched) return RECEIPT_CARD_MERCHANT_INFO;
    return {
      cardMerchantName: matched.name || RECEIPT_CARD_MERCHANT_INFO.cardMerchantName,
      cardMerchantOwnerName: matched.owner_name || RECEIPT_CARD_MERCHANT_INFO.cardMerchantOwnerName,
      cardMerchantBusinessNo: matched.business_no || RECEIPT_CARD_MERCHANT_INFO.cardMerchantBusinessNo,
      cardMerchantContact: matched.contact || matched.phone || RECEIPT_CARD_MERCHANT_INFO.cardMerchantContact,
      cardMerchantAddress: matched.address || RECEIPT_CARD_MERCHANT_INFO.cardMerchantAddress,
    };
  }, [adminPgMerchantRows]);

  // 영수증 미리보기 모달 열기 (IntegratedSettlement와 동일)
  const openReceiptPreview = useCallback(async (item) => {
    if (!item || !canShowAdminReceipt(item)) return;
    const amount = Number(item.amount || 0);
    const cardNumberMasked = '****-****-****-****';
    const taxableAmount = Math.round(amount / 1.1);
    const vatAmount = amount - taxableAmount;
    const taxFreeAmount = 0;
    const serviceAmount = 0;
    const approvalDateTime = formatKST(item.date, 'YYYY.MM.DD. HH:mm:ss');
    const cancelDateTime = item.canceledAt ? formatKST(item.canceledAt, 'YYYY.MM.DD. HH:mm:ss') : null;
    const isCanceled = !!item.cancelStatus;
    const receiptCardMerchantInfo = getReceiptCardMerchantInfo(item.pgProvider);
    const requestId = ++receiptInfoRequestRef.current;
    const previewData = {
      paymentTransactionId: item.paymentTransactionId || '',
      cardBrand: item.cardName || '-',
      cardNumberMasked,
      installmentMonth: item.type || '일시불',
      approvalNumber: item.approvalNo || '-',
      approvalDateTime,
      cancelDateTime,
      isCanceled,
      pharmacyName: item.pharmacy || '-',
      taxableAmount: taxableAmount.toLocaleString('ko-KR') + '원',
      vatAmount: vatAmount.toLocaleString('ko-KR') + '원',
      taxFreeAmount: taxFreeAmount.toLocaleString('ko-KR') + '원',
      serviceAmount: serviceAmount.toLocaleString('ko-KR') + '원',
      totalAmount: amount.toLocaleString('ko-KR') + '원',
      ...receiptSupplierInfo,
      merchantName:
        item.cpName ||
        '-',
      merchantOwnerName: item.cpOwnerName || item.driver || '-',
      merchantBusinessNo: item.cpBusinessNo || item.pharmacyBusinessNo || '-',
      merchantContact: item.cpPhone || item.pharmacyContact || '-',
      merchantAddress: item.cpAddress || item.pharmacyAddress || '-',
      ...receiptCardMerchantInfo,
    };
    let resolved;
    try {
      resolved = await resolveUnmeReceiptInfo({ role: 'admin', transactionId: item.receiptTransactionId, previewData });
    } catch (error) {
      if (requestId === receiptInfoRequestRef.current) showToast(error.message, 'error');
      return;
    }
    if (requestId !== receiptInfoRequestRef.current) return;
    setReceiptPreviewData(resolved);
    setReceiptViewMode('approval');
    setReceiptModalOpen(true);
    setReceiptEmail('');
  }, [getReceiptCardMerchantInfo, receiptSupplierInfo, showToast]);

  const handleRevealCardNumber = useCallback(async () => {
    if (!receiptPreviewData?.paymentTransactionId) {
      showToast('전표 ID가 없습니다.');
      return;
    }
    const password = (revealPassword || '').trim();
    if (!password) {
      showToast('비밀번호를 입력해 주세요.');
      return;
    }
    const reason = (revealReason || '').trim();
    if (!reason) {
      showToast('조회 사유를 입력해 주세요.');
      return;
    }
    setRevealLoading(true);
    try {
      const { data } = await axios.post(
        baseUrl + '/api/admin/payment-transactions/reveal-card',
        {
          paymentTransactionId: receiptPreviewData.paymentTransactionId,
          password,
          reason,
        },
        { withCredentials: true }
      );
      if (data?.code !== 1000) throw new Error(data?.msg || '카드번호 조회에 실패했습니다.');
      const masked = data?.data?.card_no_masked;
      if (!masked) throw new Error('카드번호를 가져오지 못했습니다.');
      setReceiptPreviewData((prev) => (prev ? { ...prev, cardNumberMasked: masked } : prev));
      setRevealModalOpen(false);
      setRevealPassword('');
      setRevealReason('');
      showToast('카드번호가 조회되었습니다.');
    } catch (e) {
      showToast(e.message || '카드번호 조회에 실패했습니다.', 'error');
    } finally {
      setRevealLoading(false);
    }
  }, [receiptPreviewData, revealPassword, revealReason, showToast]);

  const handleConfirmSendReceipt = useCallback(async () => {
    if (!receiptPreviewData?.paymentTransactionId) {
      showToast('전표 ID가 없습니다.', 'error');
      return;
    }

    const closeReceipt = () => {
      receiptInfoRequestRef.current += 1;
      setReceiptModalOpen(false);
      setReceiptEmail('');
      setReceiptPhone('');
      setSendMethod('email');
      setReceiptPreviewData(null);
      setReceiptViewMode('approval');
    };

    if (sendMethod === 'email') {
      const email = (receiptEmail || '').trim();
      if (!email) {
        showToast('전송할 이메일을 입력해주세요.', 'error');
        return;
      }
      try {
        setIsSendingReceipt(true);
        const { data } = await axios.post(
          baseUrl + '/api/admin/payment-transactions/receipt-email',
          { paymentTransactionId: receiptPreviewData.paymentTransactionId, email },
          { withCredentials: true }
        );
        if (data?.code === 1000) {
          showToast(data?.msg || '이메일로 전표가 전송되었습니다.');
          closeReceipt();
        } else {
          showToast(receiptSendError(data, data?.msg || '전표 이메일 전송에 실패했습니다.'), 'error');
        }
      } catch (err) {
        showToast(receiptSendError(err, err?.response?.data?.msg || '전표 이메일 전송 중 오류가 발생했습니다.'), 'error');
      } finally {
        setIsSendingReceipt(false);
      }
    } else if (sendMethod === 'sms') {
      const phone = (receiptPhone || '').replace(/[^0-9]/g, '');
      if (!phone) {
        showToast('휴대폰 번호를 입력해주세요.', 'error');
        return;
      }
      try {
        setIsSendingReceipt(true);
        const { data } = await axios.post(
          baseUrl + '/api/admin/payment-transactions/receipt-sms',
          { paymentTransactionId: receiptPreviewData.paymentTransactionId, phone },
          { withCredentials: true }
        );
        if (data?.code === 1000) {
          showToast(data?.msg || '전표 문자가 전송되었습니다.');
          closeReceipt();
        } else {
          showToast(receiptSendError(data, data?.data?.message || data?.msg || '전표 문자 전송에 실패했습니다.'), 'error');
        }
      } catch (err) {
        showToast(receiptSendError(err, err?.response?.data?.data?.message || err?.response?.data?.msg || '전표 문자 전송 중 오류가 발생했습니다.'), 'error');
      } finally {
        setIsSendingReceipt(false);
      }
    } else if (sendMethod === 'kakao') {
      const phone = (receiptPhone || '').replace(/[^0-9]/g, '');
      if (!phone) {
        showToast('휴대폰 번호를 입력해주세요.', 'error');
        return;
      }
      try {
        setIsSendingReceipt(true);
        const { data } = await axios.post(
          baseUrl + '/api/admin/payment-transactions/receipt-alimtalk',
          { paymentTransactionId: receiptPreviewData.paymentTransactionId, phone },
          { withCredentials: true }
        );
        if (data?.code === 1000) {
          showToast(data?.msg || '전표 알림톡을 발송했습니다.');
          closeReceipt();
        } else {
          showToast(receiptSendError(data, data?.msg || '전표 알림톡 발송에 실패했습니다.'), 'error');
        }
      } catch (err) {
        showToast(receiptSendError(err, err?.response?.data?.msg || '전표 알림톡 발송 중 오류가 발생했습니다.'), 'error');
      } finally {
        setIsSendingReceipt(false);
      }
    }
  }, [sendMethod, receiptEmail, receiptPhone, receiptPreviewData, showToast]);

  const setQuickPeriod = (type) => {
    const today = new Date();
    const formatDate = (d) => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    const parseDate = (s) => {
      const [y, m, d] = s.split('.').map(Number);
      return new Date(y, m - 1, d);
    };
    const currentStart = parseDate(startDate);
    const currentEnd = parseDate(endDate);
    let newStart, newEnd;
    switch (type) {
      case '당일': newStart = newEnd = today; break;
      case '전일':
        // 마지막날짜( endDate ) 기준 전일: 1/1~1/31 → 1/30~1/30, 다시 전일 → 1/29~1/29
        newEnd = new Date(currentEnd);
        newEnd.setDate(currentEnd.getDate() - 1);
        newStart = new Date(newEnd);
        break;
      case '당월': newStart = new Date(today.getFullYear(), today.getMonth(), 1); newEnd = today; break;
      case '전월':
        newStart = new Date(currentStart.getFullYear(), currentStart.getMonth() - 1, 1);
        newEnd = new Date(currentStart.getFullYear(), currentStart.getMonth(), 0);
        break;
      default: return;
    }
    setStartDate(formatDate(newStart));
    setEndDate(formatDate(newEnd));
  };

  const totalCount = summary.totalCount ?? 0;
  const totalAmount = summary.totalAmount ?? 0;

  // 서버가 YYYY-MM-DD를 KST 달력 날짜의 시작과 끝으로 해석한다.
  const safeStart = (startDate && !String(startDate).includes('~')) ? startDate.replace(/\./g, '-') : null;
  const safeEnd = (endDate && !String(endDate).includes('~')) ? endDate.replace(/\./g, '-') : null;
  const dateFrom = safeStart;
  const dateTo = safeEnd;

  // 결제목록 조회 (초기 또는 검색/기간 변경 시 page=1)
  const fetchList = useCallback(
    async (pageNum = 1, append = false, quiet = false) => {
      if (append && loadingMoreRef.current) return;
      const params = { dateFrom, dateTo, page: pageNum, limit: 100 };
      if (resultStatusFilter) params.status = resultStatusFilter;
      if (searchQuery.trim()) params.q = searchQuery.trim();

      if (!append && !quiet) setLoading(true);
      else {
        loadingMoreRef.current = true;
        setLoadingMore(true);
      }

      try {
        const listRes = await readPaymentList(baseUrl + '/api/admin/payment-transactions', {
          params,
          timeout: 10000,
          withCredentials: true,
        }, quiet ? visiblePagesRef.current : 1);
        const listData = listRes.data;

        if (listData?.code !== 1000) {
          if (!append && !quiet) {
            setRows([]);
            setSummary({ totalAmount: 0, totalCount: 0 });
            showToast(listData?.msg || '결제내역을 불러오는 중 오류가 발생했습니다.', 'error');
          }
          return;
        }

        const d = listData.data || {};
        let listRows = Array.isArray(d.rows) ? d.rows : Array.isArray(d) ? d : [];
        const mappedRows = listRows.map(mapPaymentTransaction);
        
        const apiSummary = d.summary || {};
        const totalCount = adminTransactionCount(apiSummary, d.total, mappedRows.length);
        const closedCount = apiSummary.closedCount ?? apiSummary.closed_count ?? null;
        const limitNum = Number(d.limit ?? params.limit ?? 100);
        const totalNum = Number(d.total ?? mappedRows.length ?? 0);
        const totalAmount = getSummaryTotalAmount(apiSummary, mappedRows, totalNum);
        const nextHasNext = d.hasNext !== undefined
          ? !!d.hasNext
          : pageNum * limitNum < totalNum;

        if (append) {
          setRows((prev) => [...prev, ...mappedRows]);
        } else {
          setRows(mappedRows);
        }
        setSummary({ totalAmount, totalCount, closedCount });
        setHasNext(nextHasNext);
        setPage(quiet ? (d.refreshedPages || 1) : pageNum);
      } catch (e) {
        if (!append && !quiet) {
          setRows([]);
          setSummary({ totalAmount: 0, totalCount: 0 });
          showToast(e?.response?.data?.msg || '결제내역을 불러오는 중 오류가 발생했습니다.', 'error');
        } else if (!quiet) {
          showToast('추가 데이터를 불러오는 데 실패했습니다.', 'error');
        }
      } finally {
        setLoading(false);
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    },
    [resultStatusFilter, dateFrom, dateTo, searchQuery, showToast]
  );

  const closePharmacyNameReasonModal = useCallback(() => {
    if (isPharmacyNameSaving) return;
    setPendingPharmacyNameChange(null);
    setPharmacyNameReason('');
  }, [isPharmacyNameSaving]);

  const confirmPharmacyNameChange = useCallback(async () => {
    if (!pendingPharmacyNameChange || isPharmacyNameSaving) return;

    const reason = pharmacyNameReason.trim();
    if (reason.length > 500) {
      showToast('변경 사유는 최대 500자까지 입력할 수 있습니다.', 'error');
      pharmacyNameReasonInputRef.current?.focus();
      return;
    }

    const payload = {
      id: pendingPharmacyNameChange.id,
      pharmacyName: pendingPharmacyNameChange.pharmacyName,
    };
    if (reason) payload.reason = reason;

    setIsPharmacyNameSaving(true);
    try {
      const { data } = await axios.post(
        baseUrl + '/api/admin/payment-transactions/pharmacy-name',
        payload,
        { withCredentials: true }
      );

      if (data?.code !== 1000) {
        throw new Error(data?.msg || '약국명 변경에 실패했습니다.');
      }

      showToast(data?.msg || '약국명이 변경되었습니다.');
      setPendingPharmacyNameChange(null);
      setPharmacyNameReason('');
      cancelEditingPharmacyName();
      await fetchList(1, false);
    } catch (err) {
      showToast(err?.response?.data?.msg || err.message || '약국명 변경 중 오류가 발생했습니다.', 'error');
    } finally {
      setIsPharmacyNameSaving(false);
    }
  }, [cancelEditingPharmacyName, fetchList, isPharmacyNameSaving, pendingPharmacyNameChange, pharmacyNameReason, showToast]);

  // 초기 조회 및 검색/기간 변경 시
  useEffect(() => {
    fetchList(1, false);
  }, [dateFrom, dateTo, searchQuery, fetchList]);

  // 무한 스크롤: IntersectionObserver로 하단 감지 (스크롤바 없을 때도 동작)
  // rows 로드 후 DOM 확정 시점에 observer 등록 (sentinel이 자식 컴포넌트에 있어 타이밍 이슈 방지)
  useEffect(() => {
    let observer = null;
    const id = requestAnimationFrame(() => {
      const el = infiniteScrollRef.current;
      if (!el) {
        return;
      }
      observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry?.isIntersecting || loadingMoreRef.current || !hasNextRef.current) return;
          fetchList(pageRef.current + 1, true);
        },
        { rootMargin: '100px', threshold: 0 }
      );
      observer.observe(el);
    });
    return () => {
      cancelAnimationFrame(id);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [fetchList, rows.length]);

  const handleExcelDownload = useCallback(async () => {
    if (isExcelDownloading) return;
    if (totalCount <= 0) {
      showToast('다운로드할 결제내역이 없습니다.');
      return;
    }

    const params = {};
    if (resultStatusFilter) params.status = resultStatusFilter;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (searchQuery.trim()) params.q = searchQuery.trim();

    setIsExcelDownloading(true);
    try {
      await downloadBlob({
        url: '/api/admin/payment-transactions/export',
        params,
        fallbackFilename: `결제내역_${dateFrom || '전체'}_${dateTo || '전체'}.xlsx`,
      });
      showToast('엑셀 다운로드가 시작되었습니다.');
    } catch (error) {
      showToast(error?.message || '엑셀 다운로드에 실패했습니다.', 'error');
    } finally {
      setIsExcelDownloading(false);
    }
  }, [resultStatusFilter, dateFrom, dateTo, isExcelDownloading, searchQuery, showToast, totalCount]);

  return (
    <>
      <Tooltip id="payment-total-tooltip" place="right" positionStrategy="absolute" className="max-w-[300px] text-[11px] z-[9999] whitespace-pre-line" isOpen={showAllOpen || hoveredTooltipId === 'payment-total'} setIsOpen={() => {}} />
      <Tooltip id="payment-excel-download-tooltip" place="left" positionStrategy="absolute" className="max-w-[300px] text-[11px] z-[9999] whitespace-pre-line" isOpen={showAllOpen || hoveredTooltipId === 'payment-excel-download'} setIsOpen={() => {}} />
      <PaymentResultMonitor rows={rows} refresh={() => fetchList(1, false, true)} />
      <ManagementPage
        title="결제내역"
        emptyStateIcon={<CreditCard size={32} className="opacity-20" />}
        hideSearch={true}
        hideDetail={true}
        filters={
          <div className="flex items-center gap-3 flex-wrap">
            <div className="h-[40px] flex items-center gap-2 bg-white px-3 rounded-lg border border-[#e5e8eb] focus-within:border-[var(--brand-primary)] transition-all shrink-0 w-[250px]">
              <input
                type="text"
                placeholder="약국명·가맹점 검색"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setSearchQuery(searchInput.trim());
                }}
                className="bg-transparent border-none outline-none text-[14px] text-[#191f28] py-0 h-full flex-1 min-w-0"
              />
              <button
                type="button"
                onClick={() => setSearchQuery(searchInput.trim())}
                className="px-2 py-1 rounded bg-[var(--brand-primary)] text-white text-[12px] font-bold hover:opacity-90 shrink-0"
              >
                검색
              </button>
            </div>
            <div className="h-4 w-[1px] bg-[#e5e8eb]" />
            <div className="h-[40px] flex items-center gap-1.5 bg-white px-3 rounded-lg border border-[#e5e8eb] focus-within:border-[var(--brand-primary)] transition-all">
              <input
                type="date"
                value={startDate.replace(/\./g, '-')}
                onChange={(e) => setStartDate(e.target.value.replace(/-/g, '.'))}
                className="bg-transparent border-none outline-none text-[14px] text-[#191f28] tabular-nums py-0 h-full"
              />
              <span className="text-[#8b95a1] text-[12px]">~</span>
              <input
                type="date"
                value={endDate.replace(/\./g, '-')}
                onChange={(e) => setEndDate(e.target.value.replace(/-/g, '.'))}
                className="bg-transparent border-none outline-none text-[14px] text-[#191f28] tabular-nums py-0 h-full"
              />
            </div>
            <div className="flex gap-1">
              {['당일', '전일', '당월', '전월'].map((type) => (
                <button
                  key={type}
                  onClick={() => setQuickPeriod(type)}
                  className="px-2.5 py-1.5 bg-white border border-[#e5e8eb] rounded-lg text-[12px] font-bold text-[#4e5968] hover:bg-[#f2f4f6] leading-none h-[40px] flex items-center"
                >
                  {type}
                </button>
              ))}
            </div>
            <PaymentStatusFilter includeClosed value={resultStatusFilter} onChange={setResultStatusFilter} className="shrink-0" />
          </div>
        }
        leftActions={
          <div className="text-[16px] font-bold text-[#191f28] flex items-center gap-1.5">
            결제 금액: <span className="text-[var(--brand-primary)]">{totalAmount.toLocaleString()}원</span> ({totalCount}건){summary.closedCount != null && Number(summary.closedCount) > 0 && <span className="ml-2 text-xs font-medium text-slate-500">결제창 종료 {summary.closedCount}건 포함</span>}
            <span
              data-tooltip-id="payment-total-tooltip"
              data-tooltip-html="조회 기간 내 결제 금액의 합계입니다. 결제창 종료 요청 금액은 제외되며 전체 건수에는 포함됩니다.<br/>당일 취소 외 취소 건은 검색 기간에 상계처리됩니다."
              onMouseEnter={() => setHoveredTooltipId('payment-total')}
              onMouseLeave={() => setHoveredTooltipId(null)}
              className="inline-flex cursor-help text-slate-400 hover:text-slate-600 transition-colors"
            >
              <HelpCircle size={14} strokeWidth={2} />
            </span>
          </div>
        }
        onExcelDownload={handleExcelDownload}
        excelButtonClassName="inline-flex items-center gap-2 px-3 py-1.5 bg-[#191f28] text-white rounded-lg font-bold text-[14px] hover:bg-black transition-colors"
        excelButtonDisabled={isExcelDownloading}
        excelButtonLabel={isExcelDownloading ? '다운로드 중...' : '엑셀다운로드'}
        excelButtonTooltipProps={{
          'data-tooltip-id': 'payment-excel-download-tooltip',
          'data-tooltip-html': '건별 결제 내역을 다운로드합니다.',
          onMouseEnter: () => setHoveredTooltipId('payment-excel-download'),
          onMouseLeave: () => setHoveredTooltipId(null),
        }}
        tableHeaders={
          <>
            <th className="px-3 py-1 w-[125px] text-[12px]">거래일시</th>
            <th className="px-3 py-1 w-[110px] text-[12px]">승인번호</th>
            <th className="px-3 py-1 min-w-[120px] text-[12px]">가맹점</th>
            {ADMIN_UNME_FALLBACK_ENABLED && <th className="px-3 py-1 w-[100px] whitespace-nowrap text-[12px]">결제 구분</th>}
            <th className="px-3 py-1 w-[220px] min-w-[220px] max-w-[220px] text-[12px]">약국</th>
            <th className="px-3 py-1 min-w-[100px] text-[12px]">영업자</th>
            <th className="px-3 py-1 w-[140px] text-[12px]">카드사</th>
            <th className="px-3 py-1 w-[160px] text-[12px]">카드번호</th>
            <th className="px-3 py-1 w-[140px] text-[12px]">할부</th>
            <th className="px-3 py-1 text-right w-[200px] text-[12px]">금액</th>
            <th className="px-3 py-1 text-right w-[200px] text-[12px]">수수료</th>
            <th className="px-3 py-1 text-[12px]">상태</th>
            <th className="px-3 py-1 w-[260px] min-w-[260px] max-w-[260px] text-[12px]">사유</th>
            <th className="px-3 py-1 text-center w-[80px] text-[12px]">전표</th>
          </>
        }
        data={rows}
        infiniteScrollRef={infiniteScrollRef}
        renderRow={(item) => {
          const isClosed = isClosedAdminPayment(item);
          const isCancelTransaction = !isClosed && item.transactionType === 'CANCEL';
          const canceledApproval = !isClosed && isCanceledApproval(item);
          const canceledAt = canceledApproval
            ? getCanceledApprovalDate(item, cancelDateMap)
            : null;
          const rowStateClass = isCancelTransaction
            ? ' bg-[#fef2f2]'
            : canceledApproval
              ? ' bg-[#f1f3f5]'
              : '';
          const displayedAmount = getSignedTransactionAmount(item);
          const fee = Number(item.fee ?? 0);
          const transactionStatus = String(item.transactionStatus || '').toUpperCase();
          const isBillingGateServerError = String(item.resultCode || '').trim().toUpperCase() === 'E999';
          const isFailed = !isClosed && !isUnknownPayment(item) && (isBillingGateServerError || transactionStatus === 'FAILED' || String(item.status || '').toUpperCase() === 'FAILED');
          const failureReason = isUnknownPayment(item) ? safePaymentResultMessage(item) : isClosed ? adminDisplayMessage(item) : isBillingGateServerError
            ? 'PG서버 오류'
            : isFailed
              ? String(item.resultMessage || '').trim()
              : '';
          const statusBadgeClass = isUnknownPayment(item) ? 'bg-amber-50 text-amber-700' : isClosed ? 'bg-slate-100 text-slate-600' : isFailed
            ? 'bg-[#fee2e2] text-[#b91c1c]'
            : transactionStatus === 'REQUESTED' || transactionStatus === 'APPROVED'
              ? 'bg-[#fff7ed] text-[#c2410c]'
              : 'bg-[#e8f3ff] text-[#1d4ed8]';
          return (
            <>
              <td className={`px-3 py-1 w-[125px] text-[#4e5968] whitespace-nowrap text-[12px] tabular-nums ${rowStateClass}`}>
                {formatKST(item.date, 'YYYY.MM.DD. HH:mm')}
              </td>
              <td className={`px-3 py-1 text-[#4e5968] whitespace-nowrap text-[12px] tabular-nums ${rowStateClass}`}>
                {item.approvalNo || '-'}
              </td>
              <td className={`px-3 py-1 min-w-[120px] text-[#4e5968] whitespace-nowrap text-[12px]${rowStateClass}`}>
                {item.merchant || '-'}
              </td>
              {ADMIN_UNME_FALLBACK_ENABLED && <td className={`px-3 py-1 whitespace-nowrap text-[12px]${rowStateClass} ${item.contractLabel === '확인 필요' || item.contractLabel === '계약 정보 확인 필요' ? 'text-amber-700' : 'text-[#4e5968]'}`}>
                {['파주', '서울'].includes(item.contractLabel)
                  ? <span className={`inline-flex rounded border px-1.5 py-0.5 font-semibold ${unmeContractColor(item.contractLabel === '파주' ? 'PRIMARY' : 'SECONDARY')}`}>{item.contractLabel}</span>
                  : item.contractLabel}
              </td>}
              <td className={`px-3 py-1 w-[220px] min-w-[220px] max-w-[220px] text-[#191f28] whitespace-nowrap text-[12px]${rowStateClass}`}>
                {editingPharmacyId === (item.id ?? item.paymentTransactionId) ? (
                  <div className="flex w-[196px] max-w-[196px] min-w-0 items-center gap-1 overflow-hidden">
                    <input
                      ref={pharmacyNameInputRef}
                      type="text"
                      value={pharmacyNameDraft}
                      maxLength={100}
                      aria-label="약국명 수정"
                      disabled={isPharmacyNameSaving}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setPharmacyNameDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          savePharmacyName(item);
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          cancelEditingPharmacyName();
                        }
                      }}
                      className="h-6 w-0 min-w-0 flex-1 rounded border border-[var(--brand-primary)] bg-white px-2 text-[12px] text-[#191f28] outline-none focus:ring-2 focus:ring-[var(--brand-primary)]/15"
                    />
                    <button
                      type="button"
                      title="저장"
                      aria-label="약국명 저장"
                      disabled={isPharmacyNameSaving}
                      onClick={(e) => {
                        e.stopPropagation();
                        savePharmacyName(item);
                      }}
                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-[var(--brand-primary)] hover:bg-[var(--brand-primary-soft)] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Check size={14} strokeWidth={2.5} />
                    </button>
                    <button
                      type="button"
                      title="취소"
                      aria-label="약국명 수정 취소"
                      disabled={isPharmacyNameSaving}
                      onClick={(e) => {
                        e.stopPropagation();
                        cancelEditingPharmacyName();
                      }}
                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-[#8b95a1] hover:bg-[#f2f4f6] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <X size={14} strokeWidth={2.5} />
                    </button>
                  </div>
                ) : (
                  <div className="flex w-[196px] max-w-[196px] min-w-0 items-center gap-1 overflow-hidden">
                    <span className="min-w-0 max-w-[164px] truncate" title={item.pharmacy || '-'}>
                      {item.pharmacy || '-'}
                    </span>
                    {TRANSACTION_PHARMACY_NAME_EDIT_ENABLED && <button
                      type="button"
                      title="약국명 수정"
                      aria-label={`${item.pharmacy || '약국'} 약국명 수정`}
                      disabled={isPharmacyNameSaving}
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditingPharmacyName(item);
                      }}
                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-[#8b95a1] hover:bg-[#f2f4f6] hover:text-[var(--brand-primary)] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Pencil size={13} strokeWidth={2} />
                    </button>}
                  </div>
                )}
              </td>
              <td className={`px-3 py-1 min-w-[100px] text-[#4e5968] whitespace-nowrap text-[12px]${rowStateClass}`}>
                {item.driver || '-'}
              </td>
              <td className={`px-3 py-1 text-[#4e5968] whitespace-nowrap text-[12px]${rowStateClass}`} title={item.cardName || ''}>
                {truncateStr(item.cardName, 8)}
              </td>
              <td className={`px-3 py-1 text-[#4e5968] whitespace-nowrap font-mono text-[12px] tabular-nums${rowStateClass}`} title={item.cardNo || ''}>
                {item.cardNo || '-'}
              </td>
              <td className={`px-3 py-1 text-[#4e5968] whitespace-nowrap text-[12px]${rowStateClass}`}>
                {item.type || '-'}
              </td>
              <td className={`px-3 py-1 text-right tabular-nums whitespace-nowrap text-[12px]${rowStateClass} ${isCancelTransaction ? 'font-semibold text-[#b91c1c]' : 'text-[#191f28]'}`}>
                {displayedAmount.toLocaleString('ko-KR')}원{isClosed && <span className="ml-2 text-[11px] font-medium text-slate-500" title="승인 금액이 아닌 요청 금액입니다.">미결제</span>}
              </td>
              <td className={`px-3 py-1 text-right text-[#4e5968] tabular-nums whitespace-nowrap text-[12px]${rowStateClass}`}>
                {fee.toLocaleString('ko-KR')}원
              </td>
              <td className={`px-3 py-1 text-[#4e5968] whitespace-nowrap text-[12px]${rowStateClass}`}>
                {isUnknownPayment(item) ? <span className="rounded bg-amber-50 px-2 py-1 text-amber-700">{unknownPaymentLabel(item)}</span> : isCancelTransaction ? (
                  <span className="inline-flex rounded bg-[#fee2e2] px-1.5 py-0.5 font-semibold text-[#b91c1c]">
                    취소
                  </span>
                ) : canceledApproval ? (
                  <>
                    <span
                      className="inline-flex items-baseline rounded bg-[#dee2e6] px-1.5 py-0.5 font-semibold text-[#4e5968]"
                      title={canceledAt
                        ? `이 승인 건은 ${formatKST(canceledAt, 'YYYY.MM.DD')}에 취소되었습니다.`
                        : '이 승인 건은 이후 취소되었습니다.'}
                    >
                      취소
                      {canceledAt && (
                        <span className="ml-1 text-[10px] font-medium text-[#6b7280]">
                          {formatKST(canceledAt, 'MM/DD')}
                        </span>
                      )}
                    </span>
                  </>
                ) : (
                  <span className={`inline-flex rounded px-1.5 py-0.5 font-semibold ${statusBadgeClass}`}>
                    {getStatusLabel(item)}
                  </span>
                )}
              </td>
              <td
                className={`px-3 py-1 w-[260px] min-w-[260px] max-w-[260px] whitespace-nowrap text-[12px]${rowStateClass} ${isFailed ? 'text-[#b91c1c]' : 'text-[#8b95a1]'}`}
                title={failureReason}
              >
                <span className="block max-w-[236px] truncate">{failureReason || '-'}{item.resultConfirmationSource === 'BILLGATE_NOTIFICATION' && <span className="block text-xs text-slate-600">PG 통지로 확인 · {item.resultConfirmedAt || '-'}</span>}</span>
              </td>
              <td className={`px-3 py-1 text-center whitespace-nowrap text-[12px]${rowStateClass}`}>
                {canShowAdminReceipt(item) && <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openReceiptPreview(item);
                  }}
                  className="px-2 py-0.5 rounded text-[12px] font-semibold text-[var(--brand-primary)] border border-[var(--brand-primary)] bg-white hover:bg-[var(--brand-primary-soft)] shrink-0"
                >
                  전표
                </button>}
              </td>
            </>
          );
        }}
      />
      {pendingPharmacyNameChange && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 px-4"
          onClick={closePharmacyNameReasonModal}
        >
          <div
            className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-[#e5e7eb] px-5 py-4">
              <h2 className="text-[16px] font-bold text-[#111827]">약국명 변경</h2>
            </div>
            <div className="space-y-4 px-5 py-4 text-[13px] text-[#374151]">
              <div className="space-y-2 rounded-xl bg-[#f9fafb] px-3 py-3">
                <div className="flex items-start justify-between gap-3">
                  <span className="shrink-0 font-semibold text-[#6b7280]">현재</span>
                  <span className="min-w-0 text-right font-medium text-[#111827] break-words">{pendingPharmacyNameChange.previousPharmacyName}</span>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <span className="shrink-0 font-semibold text-[#6b7280]">변경</span>
                  <span className="min-w-0 text-right font-bold text-[var(--brand-primary)] break-words">{pendingPharmacyNameChange.pharmacyName}</span>
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-[12px] font-semibold text-[#6b7280]">변경 사유 <span className="font-normal text-[#9ca3af]">선택</span></label>
                <textarea
                  ref={pharmacyNameReasonInputRef}
                  value={pharmacyNameReason}
                  onChange={(e) => setPharmacyNameReason(e.target.value)}
                  maxLength={500}
                  disabled={isPharmacyNameSaving}
                  rows={4}
                  placeholder="예: 담당자 입력 오류"
                  className="w-full resize-none rounded-lg border border-[#d1d5db] px-3 py-2 text-[13px] outline-none focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary)]/15 disabled:bg-[#f3f4f6]"
                />
                <div className="mt-1 text-right text-[11px] text-[#9ca3af]">{pharmacyNameReason.length}/500</div>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-[#e5e7eb] bg-[#f9fafb] px-5 py-3">
              <button
                type="button"
                onClick={closePharmacyNameReasonModal}
                disabled={isPharmacyNameSaving}
                className="rounded-xl border border-[#d1d5db] bg-white px-4 py-1.5 text-[13px] font-bold text-[#4b5563] disabled:cursor-not-allowed disabled:opacity-60"
              >
                취소
              </button>
              <button
                type="button"
                onClick={confirmPharmacyNameChange}
                disabled={isPharmacyNameSaving}
                className="rounded-xl bg-[var(--brand-primary)] px-4 py-1.5 text-[13px] font-bold text-white hover:bg-[var(--brand-primary-hover)] disabled:cursor-not-allowed disabled:bg-[var(--brand-primary-disabled)]"
              >
                {isPharmacyNameSaving ? '변경 중...' : '변경'}
              </button>
            </div>
          </div>
        </div>
      )}
      {receiptModalOpen && receiptPreviewData && RECEIPT_UI_VERSION === 2 && (
        <ReceiptPreviewModalV2
          data={receiptPreviewData}
          viewMode={receiptViewMode}
          onViewModeChange={setReceiptViewMode}
          onRevealCardNumber={() => setRevealModalOpen(true)}
          sendMethod={sendMethod}
          onSendMethodChange={setSendMethod}
          email={receiptEmail}
          onEmailChange={setReceiptEmail}
          phone={receiptPhone}
          onPhoneChange={setReceiptPhone}
          onSend={handleConfirmSendReceipt}
          onClose={() => {
            if (isSendingReceipt) return;
            receiptInfoRequestRef.current += 1;
            setReceiptModalOpen(false);
            setReceiptEmail('');
            setReceiptPhone('');
            setSendMethod('email');
            setReceiptPreviewData(null);
            setReceiptViewMode('approval');
          }}
          isSending={isSendingReceipt}
          smsEnabled={RECEIPT_SMS_ENABLED}
          kakaoEnabled={RECEIPT_KAKAO_ENABLED}
        />
      )}
      {/* 신용카드 매출전표 모달 (UI 1) */}
      {receiptModalOpen && receiptPreviewData && RECEIPT_UI_VERSION !== 2 && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1000] overflow-y-auto py-8" onClick={() => { if (isSendingReceipt) return; receiptInfoRequestRef.current += 1; setReceiptModalOpen(false); setReceiptEmail(''); setReceiptPhone(''); setSendMethod('email'); setReceiptPreviewData(null); setReceiptViewMode('approval'); }}>
          <div className="bg-white rounded border border-[#e0e0e0] w-[360px] shadow-xl my-auto flex flex-col max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white z-10 px-5 pt-4 pb-4 border-b border-[#333333] flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="text-[18px] font-bold text-[#111111] leading-tight">{RECEIPT_LABELS.titleKo}</div>
                <div className="mt-0.5 text-[11px] font-semibold leading-tight text-[#767676]">{RECEIPT_LABELS.titleEn}</div>
              </div>
              {receiptPreviewData.isCanceled && (
                <div className="flex rounded-lg overflow-hidden border border-[#e5e7eb] shrink-0">
                  <button
                    type="button"
                    onClick={() => setReceiptViewMode('approval')}
                    className={`px-3 py-1.5 text-[13px] font-semibold ${receiptViewMode === 'approval'
                      ? 'bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]'
                      : 'bg-white text-[var(--brand-primary)] border border-transparent hover:bg-[var(--brand-primary-soft)]'
                      }`}
                  >
                    승인
                  </button>
                  <button
                    type="button"
                    onClick={() => setReceiptViewMode('cancel')}
                    className={`px-3 py-1.5 text-[13px] font-semibold ${receiptViewMode === 'cancel'
                      ? 'bg-[#b91c1c] text-white border-[#b91c1c]'
                      : 'bg-white text-[#b91c1c] border border-transparent hover:bg-[#fef2f2]'
                      }`}
                  >
                    취소
                  </button>
                </div>
              )}
            </div>
            <div className="overflow-y-auto flex-1 px-5 py-5 min-w-0">
              <div className="mb-0">
                <div className="text-[16px] font-bold text-[#111111] pb-3">거래정보</div>
                <div className="text-[14px]">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">카드종류</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.cardBrand}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">카드번호</span>
                    <div className="flex items-center gap-2 ml-auto min-w-0">
                      <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.cardNumberMasked}</span>
                      <button type="button" onClick={() => setRevealModalOpen(true)} className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-[#d1d5db] text-[#4b5563] hover:bg-[#f3f4f6] shrink-0">
                        <Eye size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">할부</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.installmentMonth}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">거래유형</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptViewMode === 'approval' ? '승인' : '취소'}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">승인번호</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 tabular-nums">{receiptPreviewData.approvalNumber}</span>
                  </div>
                  <div className="flex justify-between items-start py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px] pt-0.5">{receiptViewMode === 'approval' ? '거래일시' : '취소일시'}</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 leading-snug">{receiptViewMode === 'approval' ? receiptPreviewData.approvalDateTime : (receiptPreviewData.cancelDateTime ?? '-')}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">거래처명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.pharmacyName}</span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[#e0e0e0] my-3.5" />
              <div>
                <div className="text-[16px] font-bold text-[#111111] pb-3">공급대가</div>
                <div className="text-[14px]">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">과세금액</span>
                    <span className="text-[#111111] font-bold text-right min-w-0 tabular-nums">{receiptPreviewData.taxableAmount}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">부가세</span>
                    <span className="text-[#111111] font-bold text-right min-w-0 tabular-nums">{receiptPreviewData.vatAmount}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">면세금액</span>
                    <span className="text-[#111111] font-bold text-right min-w-0 tabular-nums">{receiptPreviewData.taxFreeAmount}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">봉사료</span>
                    <span className="text-[#111111] font-bold text-right min-w-0 tabular-nums">{receiptPreviewData.serviceAmount}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#111111] font-bold text-[15px] shrink-0 w-[120px]">총 결제금액</span>
                    <span className="text-[#f04438] font-bold text-[16px] text-right min-w-0 tabular-nums">{receiptPreviewData.totalAmount}</span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[#e0e0e0] my-3.5" />
              <div>
                <div className="text-[16px] font-bold text-[#111111] pb-3">{RECEIPT_LABELS.storeInfo}</div>
                <div className="text-[14px]">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">상호명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.merchantName}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">대표자명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.merchantOwnerName}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">사업자등록번호</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 tabular-nums">{receiptPreviewData.merchantBusinessNo}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">이용문의</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.merchantContact}</span>
                  </div>
                  <div className="flex justify-between items-start py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px] pt-0.5">사업장소재지</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 break-words leading-snug">{receiptPreviewData.merchantAddress}</span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[#e0e0e0] my-3.5" />
              <div>
                <div className="text-[16px] font-bold text-[#111111] pb-3">{RECEIPT_LABELS.pspInfo}</div>
                <div className="text-[14px]">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">상호명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.supplierName}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">대표자명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.supplierOwnerName}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">사업자등록번호</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 tabular-nums">{receiptPreviewData.supplierBusinessNo}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">이용문의</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.supplierContact}</span>
                  </div>
                  <div className="flex justify-between items-start py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px] pt-0.5">사업장소재지</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 break-words leading-snug">{receiptPreviewData.supplierAddress}</span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[#e0e0e0] my-3.5" />
              <div className="mb-5">
                <div className="text-[16px] font-bold text-[#111111] pb-3">{RECEIPT_LABELS.pgInfo}</div>
                <div className="text-[14px]">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">가맹점명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.cardMerchantName}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">대표자명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.cardMerchantOwnerName}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">사업자등록번호</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 tabular-nums">{receiptPreviewData.cardMerchantBusinessNo}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">이용문의</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.cardMerchantContact}</span>
                  </div>
                  <div className="flex justify-between items-start py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px] pt-0.5">사업장소재지</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 break-words leading-snug">{receiptPreviewData.cardMerchantAddress}</span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[#e0e0e0] my-3.5" />
              <div className="space-y-2 text-[11px] leading-relaxed text-[#999999]">
                <div>본 전표는 이메일 전송용으로 구성된 내역이며, 결제/정산 시스템의 원장과 차이가 있을 수 있습니다.</div>
                <div className="text-[#666666]">{RECEIPT_CARD_SMS_NOTICE}</div>
              </div>
            </div>
            <div className="border-t border-[#eeeeee] p-5 bg-[#fafafa] shrink-0">
              <ReceiptImageActions data={receiptPreviewData} viewMode={receiptViewMode} />
              {/* 전송 방법 선택 */}
              <div className="flex rounded-lg overflow-hidden border border-[#e5e7eb] mb-3">
                <button type="button" onClick={() => setSendMethod('email')} className={`flex-1 py-2 text-[13px] font-semibold ${sendMethod === 'email' ? 'bg-[var(--brand-primary)] text-white' : 'bg-white text-[#4e5968] hover:bg-[#f2f4f6]'}`}>이메일</button>
                <button type="button" onClick={() => { if (RECEIPT_SMS_ENABLED) setSendMethod('sms'); }} className={`flex-1 py-2 text-[13px] font-semibold border-x border-[#e5e7eb] ${!RECEIPT_SMS_ENABLED ? 'cursor-not-allowed text-[#adb5bd]' : sendMethod === 'sms' ? 'bg-[#00b493] text-white' : 'bg-white text-[#4e5968] hover:bg-[#f2f4f6]'}`}>{RECEIPT_SMS_ENABLED ? '문자' : '문자(준비중)'}</button>
                <button type="button" onClick={() => { if (RECEIPT_KAKAO_ENABLED) setSendMethod('kakao'); }} className={`flex-1 py-2 text-[13px] font-semibold ${!RECEIPT_KAKAO_ENABLED ? 'cursor-not-allowed text-[#adb5bd]' : sendMethod === 'kakao' ? 'bg-[#fee500] text-[#3c1e1e]' : 'bg-white text-[#4e5968] hover:bg-[#f2f4f6]'}`}>{RECEIPT_KAKAO_ENABLED ? '카카오톡' : '카카오톡(준비중)'}</button>
              </div>
              {sendMethod === 'email' && (
                <>
                  <label className="block text-[13px] font-semibold text-[#4e5968] mb-1.5">이메일 주소</label>
                  <input
                    type="email"
                    value={receiptEmail}
                    onChange={(e) => setReceiptEmail(e.target.value)}
                    autoComplete="off"
                    placeholder="example@email.com"
                    onKeyDown={(e) => { if (e.key === 'Enter' && !isSendingReceipt) handleConfirmSendReceipt(); }}
                    className="w-full mb-3 px-3 py-1.5 rounded-lg border border-[#d1d5db] text-[14px] outline-none focus:border-[var(--brand-primary)]"
                  />
                </>
              )}
              {sendMethod === 'sms' && (
                <>
                  <label className="block text-[13px] font-semibold text-[#4e5968] mb-1.5">휴대폰 번호</label>
                  <input
                    type="tel"
                    value={receiptPhone}
                    onChange={(e) => setReceiptPhone(e.target.value)}
                    placeholder="01012345678"
                    onKeyDown={(e) => { if (e.key === 'Enter' && !isSendingReceipt) handleConfirmSendReceipt(); }}
                    className="w-full mb-3 px-3 py-1.5 rounded-lg border border-[#d1d5db] text-[14px] outline-none focus:border-[var(--brand-primary)]"
                  />
                </>
              )}
              {sendMethod === 'kakao' && (
                <>
                  <label className="block text-[13px] font-semibold text-[#4e5968] mb-1.5">휴대폰 번호</label>
                  <input type="tel" value={receiptPhone} onChange={(e) => setReceiptPhone(e.target.value)} placeholder="01012345678" onKeyDown={(e) => { if (e.key === 'Enter' && !isSendingReceipt) handleConfirmSendReceipt(); }} className="w-full mb-3 px-3 py-1.5 rounded-lg border border-[#d1d5db] text-[14px] outline-none focus:border-[var(--brand-primary)]" />
                </>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (isSendingReceipt) return;
                    receiptInfoRequestRef.current += 1;
                    setReceiptModalOpen(false);
                    setReceiptEmail('');
                    setReceiptPhone('');
                    setSendMethod('email');
                    setReceiptPreviewData(null);
                    setReceiptViewMode('approval');
                  }}
                  className="flex-1 px-3 py-1.5 rounded-lg text-[14px] font-semibold bg-[#e5e7eb] text-[#111827] hover:bg-[#d1d5db]"
                >
                  닫기
                </button>
                <button
                  type="button"
                  onClick={handleConfirmSendReceipt}
                  disabled={isSendingReceipt || (sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED)}
                  className={`flex-1 px-3 py-1.5 rounded-lg text-[14px] font-semibold ${(sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED) ? 'bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed' : sendMethod === 'kakao' ? 'bg-[#fee500] text-[#3c1e1e] hover:bg-[#f0d900]' : isSendingReceipt ? 'bg-[var(--brand-primary-disabled)] text-white cursor-not-allowed' : sendMethod === 'sms' ? 'bg-[#00b493] text-white hover:bg-[#009a7f]' : 'bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-hover)]'}`}
                >
                  {isSendingReceipt ? '전송중...' : (sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED) ? '준비중' : sendMethod === 'kakao' ? '알림톡 전송' : sendMethod === 'sms' ? '문자 전송' : '이메일 전송'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 카드번호 조회 모달 */}
      {revealModalOpen && receiptPreviewData && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-[#e5e7eb] flex items-center justify-between">
              <h2 className="text-[16px] font-bold text-[#111827]">카드번호 조회</h2>
              <button
                type="button"
                onClick={() => { if (revealLoading) return; setRevealModalOpen(false); }}
                className="text-[#9ca3af] hover:text-[#111827] text-[18px] leading-none px-1"
              >
                ×
              </button>
            </div>
            <div className="px-5 py-4 space-y-3 text-[13px] text-[#374151]">
              <p className="text-[13px] text-[#4b5563]">
                카드번호는 개인정보 보호를 위해 기본적으로 완전 마스킹 처리됩니다.
                카드번호의 앞 6자리/뒤 4자리를 확인하려면, 아래 정보를 입력해 주세요.
              </p>
              <div className="space-y-2">
                <label className="block text-[12px] font-semibold text-[#6b7280]">비밀번호</label>
                <input
                  type="password"
                  value={revealPassword}
                  onChange={(e) => setRevealPassword(e.target.value)}
                  autoComplete="off"
                  className="w-full px-3 py-1 border border-[#d1d5db] rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-[var(--brand-primary)]"
                  placeholder="로그인 비밀번호를 입력하세요"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-[12px] font-semibold text-[#6b7280]">조회 사유</label>
                <input
                  type="text"
                  value={revealReason}
                  onChange={(e) => setRevealReason(e.target.value)}
                  autoComplete="off"
                  className="w-full px-3 py-1 border border-[#d1d5db] rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-[var(--brand-primary)]"
                  placeholder="예: 카드사 문의, 고객 요청 등"
                />
              </div>
              <p className="text-[11px] text-[#9ca3af]">
                입력하신 정보는 카드번호 조회 내역과 함께 감사 로그로 기록될 수 있습니다.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-[#e5e7eb] flex justify-end gap-2 bg-[#f9fafb]">
              <button
                type="button"
                onClick={() => { if (revealLoading) return; setRevealModalOpen(false); }}
                className="px-4 py-1 rounded-xl text-[13px] font-bold bg-white text-[#4b5563] border border-[#d1d5db]"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleRevealCardNumber}
                disabled={revealLoading}
                className="px-4 py-1 rounded-xl text-[13px] font-bold bg-[#111827] text-white disabled:opacity-60"
              >
                {revealLoading ? '조회 중...' : '조회'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PaymentList;
